// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import { Alert } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { THForm } from '@Molecules/THForm/THForm';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { copyToClipboard } from '@Helpers/copy';

import './LicenseForm.scss';

export interface LicenseValuesForm {
    license: string;
}

interface LicenseFormProps {
    form: FormInstance;
    isValid: boolean;
    challenge?: string;
    onFinish(values: LicenseValuesForm): void;
    onChange: () => void;
    onCancel: () => void;
}

export const LicenseForm = ({
    form,
    isValid,
    challenge,
    onFinish,
    onChange,
    onCancel,
}: LicenseFormProps): ReactElement => {
    const i = useLocalMessage();

    return (
        <THForm<LicenseValuesForm>
            name="license-form"
            onFinish={onFinish}
            form={form}
            onValuesChange={onChange}
            initialValues={{
                challenge,
            }}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        onCancel={onCancel}
                        disabled={!isValid}
                        footerText={i(`license.set.form.submit`)}
                    />
                }
            >
                <Alert className="license-form__info" message={i('license.set.form.info')} type="warning" />
                <THForm.Item
                    name="challenge"
                    label={i('license.set.form.challenge.label')}
                    extraAction={
                        <THButton
                            type="link"
                            size="small"
                            className="license-form__copy-button"
                            onClick={() => copyToClipboard(challenge || '', 'Challenge copied to clipboard!')}
                        >
                            Copy this challenge
                        </THButton>
                    }
                >
                    <THTextArea
                        className="license-form__challenge"
                        borderless
                        disabled
                        placeholder={i('license.set.form.license.placeholder')}
                    />
                </THForm.Item>

                <THForm.Item
                    label={i('license.set.form.license.label')}
                    name="license"
                    rules={[{ required: true, message: i('license.set.form.license.required') }]}
                >
                    <THTextArea placeholder={i('license.set.form.license.placeholder')} />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
